// src/EstimateModal.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const panel = {
  hidden: { opacity: 0, y: 18, scale: 0.985, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: 10,
    scale: 0.985,
    filter: "blur(8px)",
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

const stepSwap = {
  hidden: { opacity: 0, y: 10, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: "blur(8px)",
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] uppercase tracking-[0.16em] text-white/60">
        {label} {required ? <span className="text-[#D84B3F]">*</span> : null}
      </label>
      {children}
    </div>
  );
}

const Input = React.forwardRef(function Input(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className={
        "w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 " +
        "focus:outline-none focus:ring-2 focus:ring-[#D84B3F]/60 focus:border-[#D84B3F]/40 transition"
      }
    />
  );
});

function Select({ value, onChange, options = [] }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={
        "w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white " +
        "focus:outline-none focus:ring-2 focus:ring-[#D84B3F]/60 focus:border-[#D84B3F]/40 transition"
      }
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0B1020]">
          {o}
        </option>
      ))}
    </select>
  );
}

function StepPill({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={[
          "h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-mono",
          active
            ? "border-[#D84B3F]/60 bg-[#D84B3F] text-white shadow-[0_0_18px_rgba(216,75,63,0.45)]"
            : done
            ? "border-white/20 bg-white/10 text-white/80"
            : "border-white/15 bg-white/5 text-white/55",
        ].join(" ")}
      >
        {n}
      </div>
      <span
        className={[
          "text-[11px] uppercase tracking-[0.18em]",
          active || done ? "text-white/80" : "text-white/45",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

function parsePlaceToAddress(place) {
  const comps = place?.address_components || [];
  const get = (type) => comps.find((c) => c.types?.includes(type))?.long_name || "";
  const getShort = (type) => comps.find((c) => c.types?.includes(type))?.short_name || "";

  const streetNumber = get("street_number");
  const route = get("route");
  const street = [streetNumber, route].filter(Boolean).join(" ").trim();

  const city =
    get("locality") ||
    get("sublocality") ||
    get("postal_town") ||
    get("administrative_area_level_2");

  const state = getShort("administrative_area_level_1");
  const zip = get("postal_code");

  return { street, city, state, zip };
}

// Google Places Autocomplete input
function AddressAutocomplete({ value, onPick, onChange, active }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    // Only init when visible/active
    if (!active) return;
    if (!window.google?.maps?.places || !inputRef.current) return;

    // Prevent double-init
    if (acRef.current) return;

    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });

    // Make sure we get address components
    if (ac.setFields) ac.setFields(["address_components", "formatted_address"]);

    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const formatted = place?.formatted_address || inputRef.current.value;
      onPick(place, formatted);
    });

    acRef.current = { ac, listener };

    return () => {
      try {
        listener?.remove?.();
        window.google?.maps?.event?.clearInstanceListeners?.(ac);
      } catch {}
      acRef.current = null;
    };
  }, [active, onPick]);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-2.5 text-white/35 text-sm">⌕</span>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search address"
        style={{ paddingLeft: "2.1rem" }}
        autoComplete="off"
      />
    </div>
  );
}

export function EstimateModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  const SERVICE_TYPES = useMemo(
    () => [
      "Free Roof Inspection",
      "Residential Roofing",
      "Commercial Roofing",
      "Gutter Repair & Install",
      "Storm / Hail Inspection",
      "Leak Repair",
      "Other",
    ],
    []
  );

  const initial = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      addressSearch: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      serviceType: "Free Roof Inspection",
      notes: "",
      consent: false,
    }),
    []
  );

  const [form, setForm] = useState(initial);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const close = () => {
    if (loading) return;
    setStep(1);
    setSent(false);
    setError("");
    setForm(initial);
    onClose?.();
  };

  // Escape closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading]);

  // Lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Force Google suggestions above everything
  useEffect(() => {
    if (!open) return;
    const styleId = "pac-zfix";
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.innerHTML = `.pac-container { z-index: 2147483647 !important; }`;
      document.head.appendChild(s);
    }
  }, [open]);

  // CRITICAL: prevent backdrop from stealing click when user clicks a Places suggestion
  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      // Capture phase: if click is inside pac-container, stop it before backdrop sees it
      const path = e.composedPath ? e.composedPath() : [];
      const inPac =
        (path && path.some((n) => n?.classList?.contains?.("pac-container"))) ||
        e.target?.closest?.(".pac-container");

      if (inPac) {
        e.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handler, true);
    document.addEventListener("touchstart", handler, true);

    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [open]);

  const canNext =
    (step === 1 &&
      form.firstName.trim().length > 1 &&
      form.lastName.trim().length > 1 &&
      form.phone.trim().length >= 7 &&
      form.email.trim().includes("@")) ||
    (step === 2 &&
      form.addressSearch.trim().length > 4 &&
      form.street.trim().length > 4 &&
      form.city.trim().length > 1 &&
      form.state.trim().length >= 2 &&
      form.zip.trim().length >= 5) ||
    (step === 3 && form.consent);

  const next = () => {
    if (!canNext || loading) return;
    setError("");
    setStep((s) => Math.min(3, s + 1));
  };

  const back = () => {
    if (loading) return;
    setError("");
    if (step === 1) close();
    else setStep((s) => Math.max(1, s - 1));
  };

  const onPickPlace = useCallback((place, formatted) => {
    const parsed = parsePlaceToAddress(place);
    setForm((p) => ({
      ...p,
      addressSearch: formatted || p.addressSearch,
      street: parsed.street || p.street,
      city: parsed.city || p.city,
      state: parsed.state || p.state,
      zip: parsed.zip || p.zip,
    }));
  }, []);

  // ✅ Fire-and-forget write that NEVER blocks UX
const submit = async () => {
  if (loading) return;

  setError("");

  // Basic validation
  if (
    form.firstName.trim().length < 2 ||
    form.lastName.trim().length < 2 ||
    form.phone.trim().length < 7 ||
    !form.email.includes("@") ||
    form.street.trim().length < 4 ||
    form.city.trim().length < 2 ||
    form.state.trim().length < 2 ||
    form.zip.trim().length < 5
  ) {
    setError("Please complete the required fields.");
    return;
  }

  if (!form.consent) {
    setError("Please check the consent box to continue.");
    return;
  }

  // ✅ instantly show confirmation screen
  setSent(true);
  setLoading(false);

  // ✅ sanitize values for GHL
  const firstName = (form.firstName || "").trim();
  const lastName = (form.lastName || "").trim();
  const email = (form.email || "").trim();
  const phone = (form.phone || "").replace(/[^\d+]/g, "").trim(); // keep digits and +

  const address1 = (form.street || "").trim();
  const city = (form.city || "").trim();
  const state = (form.state || "").trim();
  const postalCode = (form.zip || "").trim();
  const serviceType = form.serviceType;
  const notes = (form.notes || "").trim();

  // Background attempt to write + webhook (do NOT block UI)
  try {
    setLoading(true);

    // 1) Firestore write (background)
    const writePromise = addDoc(collection(db, "roofingLeads"), {
      ...form,
      fullName: `${firstName} ${lastName}`.trim(),
      address: `${address1}, ${city}, ${state} ${postalCode}`.trim(),
      createdAt: serverTimestamp(),
      source: "roofingpro-landing",
    });

    // 2) GHL webhook (background, do NOT await)
    if (!email && !phone) {
      console.warn("Skipping GHL webhook: missing email and phone");
    } else {
      fetch(
        "https://services.leadconnectorhq.com/hooks/VjdHckTzy4zivZgzIosZ/webhook-trigger/a501dce8-7d27-46a6-92b0-811cc67d3ee0",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            phone,
            address1,
            city,
            state,
            postalCode,
            serviceType,
            notes,
            source: "roofingprohtx.com",
          }),
        }
      ).catch((e) => {
        console.warn("GHL webhook failed:", e);
      });
    }

    // Only wait a little for Firestore so we don't hang
    await Promise.race([
      writePromise,
      new Promise((resolve) => setTimeout(resolve, 4000)),
    ]);
  } catch (err) {
    console.warn("Background Firestore write failed:", err);
  } finally {
    setLoading(false);
  }
};

  return (
    <AnimatePresence mode="wait">
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
          initial="hidden"
          animate="show"
          exit="exit"
          variants={backdrop}
        >
          {/* Backdrop: use onClick (NOT onMouseDown) so Places selection works */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={(e) => {
              // If click came from the Google dropdown, ignore.
              const path = e.nativeEvent?.composedPath?.() || [];
              const inPac =
                path.some((n) => n?.classList?.contains?.("pac-container")) ||
                e.target?.closest?.(".pac-container");
              if (inPac) return;
              close();
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-[#0b2a6a]/20" />

          <motion.div
            variants={panel}
            className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-[#0B1020] shadow-[0_30px_120px_rgba(0,0,0,0.75)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pointer-events-none absolute -top-28 -left-24 h-72 w-72 rounded-full bg-white/10 blur-[90px]" />
            <div className="pointer-events-none absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-[#D84B3F]/20 blur-[110px]" />

            {/* Header */}
            <div className="relative border-b border-white/10 px-6 py-5">
              <button
                type="button"
                onClick={close}
                className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:bg-white/10 hover:text-white transition"
              >
                Close
              </button>

              <p className="text-[11px] uppercase tracking-[0.22em] text-white/60">
                Free inspection · photo proof · clear options
              </p>
              <h3 className="mt-1 text-lg font-semibold text-white">Request Your Free Inspection</h3>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <StepPill n={1} label="Contact" active={step === 1} done={step > 1} />
                <div className="h-px w-6 bg-white/10 hidden sm:block" />
                <StepPill n={2} label="Address" active={step === 2} done={step > 2} />
                <div className="h-px w-6 bg-white/10 hidden sm:block" />
                <StepPill n={3} label="Details" active={step === 3} done={false} />
              </div>
            </div>

            {/* Body */}
            <div className="relative px-6 py-6">
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center"
                >
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#D84B3F]/20 border border-[#D84B3F]/30">
                    <span className="text-white text-xl">✓</span>
                  </div>
                  <h4 className="text-lg font-semibold text-white">Thank you!</h4>
                  <p className="mt-2 text-sm text-white/70">
                    You’ll be receiving a call shortly to schedule your inspection.
                  </p>

                  <button
                    type="button"
                    onClick={close}
                    className="mt-5 inline-flex rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white transition"
                  >
                    Close
                  </button>
                </motion.div>
              ) : (
                <>
                  <AnimatePresence mode="wait">
                    {step === 1 && (
                      <motion.div
                        key="s1"
                        variants={stepSwap}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="space-y-4"
                      >
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="First Name" required>
                            <Input value={form.firstName} onChange={set("firstName")} placeholder="First name" />
                          </Field>
                          <Field label="Last Name" required>
                            <Input value={form.lastName} onChange={set("lastName")} placeholder="Last name" />
                          </Field>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Phone" required>
                            <Input value={form.phone} onChange={set("phone")} placeholder="(###) ###-####" />
                          </Field>
                          <Field label="Email" required>
                            <Input value={form.email} onChange={set("email")} placeholder="you@email.com" type="email" />
                          </Field>
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="s2"
                        variants={stepSwap}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="space-y-4"
                      >
                        <Field label="Search address" required>
                          <AddressAutocomplete
                            value={form.addressSearch}
                            onChange={(v) => setForm((p) => ({ ...p, addressSearch: v }))}
                            onPick={onPickPlace}
                            active={open && step === 2}
                          />
                          <p className="mt-2 text-[11px] text-white/45">
                            Start typing and select your address to autofill details.
                          </p>
                        </Field>

                        <Field label="Street Address" required>
                          <Input value={form.street} onChange={set("street")} placeholder="Street address" />
                        </Field>

                        <div className="grid gap-4 sm:grid-cols-3">
                          <Field label="City" required>
                            <Input value={form.city} onChange={set("city")} placeholder="City" />
                          </Field>
                          <Field label="State" required>
                            <Input value={form.state} onChange={set("state")} placeholder="State" />
                          </Field>
                          <Field label="Zip" required>
                            <Input value={form.zip} onChange={set("zip")} placeholder="Zip" />
                          </Field>
                        </div>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        key="s3"
                        variants={stepSwap}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="space-y-4"
                      >
                        <Field label="What do you need?" required>
                          <Select value={form.serviceType} onChange={set("serviceType")} options={SERVICE_TYPES} />
                        </Field>

                        <Field label="Notes (optional)">
                          <textarea
                            value={form.notes}
                            onChange={set("notes")}
                            rows={4}
                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#D84B3F]/60 focus:border-[#D84B3F]/40 transition"
                            placeholder="Leaks, storm damage, timeline, anything helpful…"
                          />
                        </Field>

                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <label className="flex items-start gap-3 text-xs text-white/75">
                            <input
                              type="checkbox"
                              checked={form.consent}
                              onChange={set("consent")}
                              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/40 text-[#D84B3F] focus:ring-[#D84B3F]"
                            />
                            <span>
                              By checking this box, I consent to receive transaction messages related to services I
                              requested. Message &amp; data rates may apply. Reply HELP for help or STOP to opt out.
                            </span>
                          </label>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {error ? (
                    <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-2 text-xs text-red-200">
                      {error}
                    </div>
                  ) : null}

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={back}
                      className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/70 hover:bg-white/10 hover:text-white transition"
                    >
                      {step === 1 ? "Cancel" : "Back"}
                    </button>

                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={next}
                        disabled={!canNext || loading}
                        className="rounded-full bg-[#D84B3F] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.35)] hover:bg-[#c94439] transition disabled:opacity-50"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={submit}
                        disabled={loading}
                        className="rounded-full bg-[#D84B3F] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_18px_45px_rgba(216,75,63,0.45)] hover:bg-[#c94439] transition disabled:opacity-50"
                      >
                        {loading ? "Submitting..." : "Request My Inspection"}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}